import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, Receipt, Banknote, Stethoscope } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => (n != null ? `$${(+n).toFixed(2)}` : "—");

/**
 * Dialog para ver el detalle de una CxC o CxP con todos sus cobros/pagos
 * aplicados y retenciones (en el caso de CxC). Incluye botón "Exportar".
 *
 * Props:
 *   data:   resultado de GET /accounting/cuentas/{id}/detalle o cuentas-pagar/{id}/detalle
 *   kind:   "cxc" | "cxp"
 *   open:   bool
 *   onClose: fn
 */
export default function DetalleCuentaDialog({ data, kind, open, onClose, onRefresh }) {
  const [diagnostico, setDiagnostico] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  if (!data) return null;
  const isCxc = kind === "cxc";
  const movs = isCxc ? data.cobros : data.pagos;
  const entityNombre = isCxc ? data.cliente_nombre : data.proveedor_nombre;
  const entityLabel = isCxc ? "Cliente" : "Proveedor";
  const numero = data.numero_factura || data.factura_numero;
  const total = +data.monto_total || 0;
  const pagado = +data.monto_pagado || 0;
  const retencion = +data.retencion_aplicada || 0;
  const ncAplicada = +data.nc_aplicada_valor || 0;
  const saldo = +data.saldo || 0;

  const exportarCSV = () => {
    const rows = [];
    rows.push([entityLabel, entityNombre || ""]);
    rows.push(["Factura", numero || ""]);
    rows.push(["Fecha factura", data.fecha_factura || ""]);
    rows.push(["Vencimiento", data.fecha_vencimiento || ""]);
    rows.push(["Monto total", total.toFixed(2)]);
    rows.push(["Total pagado", pagado.toFixed(2)]);
    if (isCxc) rows.push(["Retención aplicada", retencion.toFixed(2)]);
    if (isCxc) rows.push(["NC aplicada", ncAplicada.toFixed(2)]);
    rows.push(["Saldo", saldo.toFixed(2)]);
    rows.push(["Estado", data.estado || ""]);
    rows.push([]);
    rows.push([`Detalle de ${isCxc ? "cobros" : "pagos"}:`]);
    rows.push([
      isCxc ? "N° Cobro" : "N° Pago",
      "Fecha",
      "Método",
      "Referencia",
      isCxc ? "Monto cobro total" : "Monto pago total",
      isCxc ? "Aplicado a esta CxC" : "Aplicado a esta CxP",
      "Observaciones",
      "Usuario",
    ]);
    (movs || []).forEach(m => {
      rows.push([
        m[isCxc ? "numero_cobro" : "numero_pago"] || "",
        m.fecha || "",
        m.metodo || "",
        m.referencia || "",
        (m[isCxc ? "monto_total_cobro" : "monto_total_pago"] || 0).toFixed(2),
        (m[isCxc ? "monto_aplicado_a_esta_cxc" : "monto_aplicado_a_esta_cxp"] || 0).toFixed(2),
        (m.observaciones || "").replace(/[\r\n,]+/g, " "),
        m.user_email || "",
      ]);
    });
    if (isCxc && (data.notas_credito_aplicadas || []).length > 0) {
      rows.push([]);
      rows.push(["Notas de Crédito aplicadas a esta factura:"]);
      rows.push(["N° NC", "Fecha", "Motivo", "Subtotal", "IVA", "Total NC", "Estado SRI"]);
      data.notas_credito_aplicadas.forEach(nc => {
        rows.push([
          nc.numero || "",
          nc.fecha || "",
          (nc.motivo || "").replace(/[\r\n,]+/g, " "),
          (+nc.subtotal || 0).toFixed(2),
          (+nc.iva || 0).toFixed(2),
          (+nc.total || 0).toFixed(2),
          nc.estado_sri || "",
        ]);
      });
    }
    if (isCxc && (data.retenciones_recibidas_detalle || []).length > 0) {
      rows.push([]);
      rows.push(["Retenciones recibidas del cliente:"]);
      rows.push(["N° Comprobante", "Fecha", "Base IR", "% IR", "Valor IR", "Base IVA", "% IVA", "Valor IVA", "Total"]);
      data.retenciones_recibidas_detalle.forEach(r => {
        rows.push([
          r.numero_comprobante || "",
          r.fecha || "",
          (+r.base_ir || 0).toFixed(2),
          (+r.porcentaje_ir || 0).toFixed(2),
          (+r.valor_retenido_ir || 0).toFixed(2),
          (+r.base_iva || 0).toFixed(2),
          (+r.porcentaje_iva || 0).toFixed(2),
          (+r.valor_retenido_iva || 0).toFixed(2),
          ((+r.valor_retenido_ir || 0) + (+r.valor_retenido_iva || 0)).toFixed(2),
        ]);
      });
    }
    const csv = "\ufeff" + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${isCxc ? "cxc" : "cxp"}-${numero || data.id}-detalle.csv`;
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={`detalle-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCxc ? <Receipt size={18} className="text-[#205c45]" /> : <Banknote size={18} className="text-[#205c45]" />}
            Detalle {isCxc ? "Cuenta por Cobrar" : "Cuenta por Pagar"} · {numero}
            {isCxc && (
              <Button
                size="sm" variant="outline" className="ml-auto h-7 text-xs"
                onClick={async () => {
                  setDiagLoading(true);
                  try {
                    const { data: d } = await api.get(`/accounting/cuentas/${data.id}/diagnostico-saldo`);
                    setDiagnostico(d);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setDiagLoading(false); }
                }}
                disabled={diagLoading}
                data-testid="diagnosticar-saldo-btn"
                title="Detectar y corregir saldos incorrectos (doble conteo, NCs no reflejadas, etc.)"
              >
                {diagLoading ? <Loader2 className="animate-spin" size={12} /> : <Stethoscope size={12} className="mr-1" />}
                Diagnosticar saldo
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {data.loading ? (
          <div className="text-center py-8"><Loader2 className="animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Field label={entityLabel} value={entityNombre} />
              <Field label="Fecha factura" value={data.fecha_factura} />
              <Field label="Vencimiento" value={data.fecha_vencimiento || "—"} />
              <Field label="Estado" value={<Badge variant="outline" className="capitalize">{data.estado}</Badge>} />
            </div>

            {/* Resumen totales */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-[#f5f8f6] rounded p-3">
              <NumCard label="Monto total" value={fmt(total)} />
              <NumCard label="Pagado" value={fmt(pagado)} color="#205c45" />
              {retencion > 0 && <NumCard label={isCxc ? "Retención" : "Retenido"} value={fmt(retencion)} color="#a55700" testId="detalle-retencion-card" />}
              {isCxc && ncAplicada > 0 && <NumCard label="NC aplicada" value={fmt(ncAplicada)} color="#5c7368" />}
              <NumCard label="Saldo" value={fmt(saldo)} color={saldo > 0.01 ? "#b53a25" : "#205c45"} bold />
            </div>

            {/* Lista de cobros/pagos */}
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1">
                {isCxc ? "Cobros aplicados" : "Pagos aplicados"} ({movs?.length || 0})
              </div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#f7f9f8] text-[11px]">
                    <tr>
                      <th className="text-left p-2">{isCxc ? "N° Cobro" : "N° Pago"}</th>
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Método</th>
                      <th className="text-left p-2">Referencia</th>
                      <th className="text-right p-2">{isCxc ? "Total cobro" : "Total pago"}</th>
                      <th className="text-right p-2">Aplicado a esta {isCxc ? "CxC" : "CxP"}</th>
                      <th className="text-left p-2">Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(movs || []).map(m => (
                      <tr key={m.id} className="border-t border-[#eee]">
                        <td className="p-2 font-mono text-xs">{m[isCxc ? "numero_cobro" : "numero_pago"] || "—"}</td>
                        <td className="p-2 text-xs">{m.fecha}</td>
                        <td className="p-2 capitalize">{m.metodo}</td>
                        <td className="p-2 text-xs text-[#5c7368]">{m.referencia || "—"}</td>
                        <td className="p-2 text-right tabular-nums">{fmt(m[isCxc ? "monto_total_cobro" : "monto_total_pago"])}</td>
                        <td className="p-2 text-right tabular-nums font-semibold text-[#205c45]">{fmt(m[isCxc ? "monto_aplicado_a_esta_cxc" : "monto_aplicado_a_esta_cxp"])}</td>
                        <td className="p-2 text-[10px] text-[#5c7368]">{m.user_email || "—"}</td>
                      </tr>
                    ))}
                    {(!movs || movs.length === 0) && (
                      <tr><td colSpan={7} className="text-center py-6 text-[#8ba397]">Sin {isCxc ? "cobros" : "pagos"} registrados aún</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notas de Crédito aplicadas (solo CxC) */}
            {isCxc && (data.notas_credito_aplicadas || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1">
                  Notas de Crédito aplicadas a esta factura ({data.notas_credito_aplicadas.length})
                </div>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm" data-testid="cxc-nc-aplicadas-table">
                    <thead className="bg-[#f7f9f8] text-[11px]">
                      <tr>
                        <th className="text-left p-2">N° NC</th>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Motivo</th>
                        <th className="text-right p-2">Subtotal</th>
                        <th className="text-right p-2">IVA</th>
                        <th className="text-right p-2">Total NC</th>
                        <th className="text-left p-2">Estado SRI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.notas_credito_aplicadas.map(nc => (
                        <tr key={nc.id} className="border-t border-[#eee]" data-testid={`nc-aplicada-row-${nc.numero}`}>
                          <td className="p-2 font-mono text-xs">{nc.numero}</td>
                          <td className="p-2 text-xs">{nc.fecha}</td>
                          <td className="p-2 text-xs">{nc.motivo || "—"}</td>
                          <td className="p-2 text-right tabular-nums">{fmt(nc.subtotal)}</td>
                          <td className="p-2 text-right tabular-nums">{fmt(nc.iva)}</td>
                          <td className="p-2 text-right tabular-nums font-semibold text-[#205c45]">{fmt(nc.total)}</td>
                          <td className="p-2 text-xs">
                            <Badge variant="outline" className="capitalize">{nc.estado_sri || "—"}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Retenciones recibidas (solo CxC) */}
            {isCxc && (data.retenciones_recibidas_detalle || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1">
                  Retenciones recibidas del cliente
                </div>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f7f9f8] text-[11px]">
                      <tr>
                        <th className="text-left p-2">N° Comprobante</th>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-right p-2">Valor IR</th>
                        <th className="text-right p-2">Valor IVA</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.retenciones_recibidas_detalle.map(r => (
                        <tr key={r.id} className="border-t border-[#eee]">
                          <td className="p-2 font-mono text-xs">{r.numero_comprobante}</td>
                          <td className="p-2 text-xs">{r.fecha}</td>
                          <td className="p-2 text-right tabular-nums">{fmt(r.valor_retenido_ir)}</td>
                          <td className="p-2 text-right tabular-nums">{fmt(r.valor_retenido_iva)}</td>
                          <td className="p-2 text-right tabular-nums font-semibold text-[#a55700]">
                            {fmt((+r.valor_retenido_ir || 0) + (+r.valor_retenido_iva || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ITER186: Retenciones emitidas al proveedor (solo CxP) */}
            {!isCxc && (data.retenciones_emitidas_detalle || []).length > 0 && (
              <div data-testid="cxp-retenciones-emitidas-block">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1">
                  Retenciones emitidas al proveedor ({data.retenciones_emitidas_detalle.length})
                </div>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f7f9f8] text-[11px]">
                      <tr>
                        <th className="text-left p-2">N° Retención</th>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Factura</th>
                        <th className="text-right p-2">Total retenido</th>
                        <th className="text-left p-2">Estado SRI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.retenciones_emitidas_detalle.map(r => (
                        <tr key={r.id} className="border-t border-[#eee]" data-testid={`ret-emitida-row-${r.numero || r.id}`}>
                          <td className="p-2 font-mono text-xs">{r.numero || "—"}</td>
                          <td className="p-2 text-xs">{r.fecha}</td>
                          <td className="p-2 text-xs">{r.factura_proveedor_numero || "—"}</td>
                          <td className="p-2 text-right tabular-nums font-semibold text-[#a55700]">{fmt(r.total_retenido)}</td>
                          <td className="p-2 text-xs">
                            <Badge variant="outline" className="capitalize">{r.estado_sri || "—"}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.observaciones && (
              <div className="bg-[#f5f8f6] rounded p-3 text-sm">
                <div className="text-[10px] uppercase text-[#5c7368] mb-1">Observaciones</div>
                <div className="whitespace-pre-wrap">{data.observaciones}</div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={exportarCSV} disabled={!movs} data-testid={`exportar-detalle-${kind}`}>
            <Download size={14} className="mr-1" /> Exportar detalle (CSV)
          </Button>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>

      {/* ITER147: Sub-dialog de diagnóstico de saldo */}
      {diagnostico && (
        <Dialog open={!!diagnostico} onOpenChange={(v) => { if (!v) setDiagnostico(null); }}>
          <DialogContent className="max-w-2xl" data-testid="diagnostico-saldo-dialog">
            <DialogHeader>
              <DialogTitle>Diagnóstico de saldo · Factura {diagnostico.factura}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {!diagnostico.tiene_discrepancia ? (
                <div className="bg-[#e9f3ec] border border-[#a4d9b3] rounded p-3 text-[#205c45]">
                  ✓ El saldo de esta CxC está correcto. No se detectaron discrepancias.
                </div>
              ) : (
                <div className="bg-[#fff4e6] border border-[#f5c684] rounded p-3 text-[#a55700]">
                  <div className="font-semibold mb-1">⚠ Discrepancias detectadas:</div>
                  <ul className="list-disc pl-5 space-y-1">
                    {diagnostico.discrepancias.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded p-3">
                  <div className="text-[10px] uppercase text-[#5c7368] mb-2">Actual en BD</div>
                  <div className="space-y-1">
                    <Row label="Monto total" value={fmt(diagnostico.monto_total)} />
                    <Row label="Retención" value={fmt(diagnostico.retencion_aplicada)} />
                    <Row label="Pagado" value={fmt(diagnostico.monto_pagado_actual)} />
                    <Row label="Saldo" value={fmt(diagnostico.saldo_actual)} bold />
                    <Row label="Estado" value={diagnostico.estado_actual} />
                  </div>
                </div>
                <div className="border rounded p-3 bg-[#f3f8f5]">
                  <div className="text-[10px] uppercase text-[#205c45] mb-2 font-semibold">Calculado real</div>
                  <div className="space-y-1">
                    <Row label="Cobros sumados" value={fmt(diagnostico.cobros_aplicados_total)} />
                    <Row label="NCs aplicadas" value={fmt(diagnostico.ncs_aplicadas_total)} />
                    <Row label="Pagado real" value={fmt(diagnostico.monto_pagado_calculado)} bold />
                    <Row label="Saldo real" value={fmt(diagnostico.saldo_calculado)} bold />
                    <Row label="Estado correcto" value={diagnostico.estado_calculado} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDiagnostico(null)}>Cerrar</Button>
              {diagnostico.tiene_discrepancia && (
                <Button
                  className="bg-[#205c45] hover:bg-[#1a4a37]"
                  data-testid="aplicar-recalculo-btn"
                  onClick={async () => {
                    try {
                      const { data: res } = await api.post(`/accounting/cuentas/${data.id}/recalcular-completo`);
                      if (res.sin_cambios) toast.message(res.mensaje);
                      else toast.success(`✓ Saldo corregido: pagado $${res.despues.monto_pagado.toFixed(2)}, saldo $${res.despues.saldo.toFixed(2)}`);
                      setDiagnostico(null);
                      onRefresh && onRefresh();
                      onClose && onClose();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                >
                  Aplicar corrección
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between text-xs">
    <span className="text-[#5c7368]">{label}:</span>
    <span className={`tabular-nums ${bold ? "font-semibold text-[#15241d]" : ""}`}>{value}</span>
  </div>
);

function Field({ label, value }) {
  return (
    <div className="bg-[#f5f8f6] rounded p-2">
      <div className="text-[10px] uppercase text-[#5c7368]">{label}</div>
      <div className="font-semibold">{value || "—"}</div>
    </div>
  );
}

function NumCard({ label, value, color, bold, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <div className="text-[10px] uppercase text-[#5c7368]">{label}</div>
      <div className={`tabular-nums ${bold ? "text-lg font-bold" : "font-semibold"}`} style={{ color: color || "#15241d" }}>{value}</div>
    </div>
  );
}
